"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { useOverlayPortal } from "../overlay-portal";
import { cn } from "../utils";

/**
 * App-wide tooltip body (same as Agent-Chat / MiniHintTooltip). Do not override
 * font size on TooltipContent; use `max-w-*` only if a narrower cap is needed.
 */
export const TOOLTIP_CONTENT_APP_CLASS_NAME =
  "max-w-[min(280px,calc(100vw-2rem))] px-2 py-1 text-left text-[10px] leading-none text-popover-foreground";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

/**
 * Disabled controls swallow pointer events, so a hint on a wrapper around one
 * (`<TooltipTrigger asChild><span><button disabled /></span>`) never opens.
 * The trigger switches pointer events off on a disabled direct child, so the
 * hover lands on the wrapper.
 */
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    className={cn("[&>:disabled]:pointer-events-none", className)}
    {...props}
  />
));
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    /** Render inside this element instead of body (fixes Tooltip inside Dialog) */
    container?: HTMLElement | null;
  }
>(({ className, sideOffset = 4, container, ...props }, ref) => {
  const portal = useOverlayPortal(container);
  return (
    <TooltipPrimitive.Portal container={portal}>
      <TooltipPrimitive.Content
        collisionPadding={12}
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "pointer-events-none z-50 overflow-hidden rounded-md border border-border/60 bg-popover shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          TOOLTIP_CONTENT_APP_CLASS_NAME,
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
